import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import { Box, Button, CardMedia, IconButton, SxProps, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DeleteIcon } from '../../../assets/icon-delete.svg';
import { CreditChip, ShipsFromNote, ViewDetailsSidebar, WalletMeter } from '../../../components/_mui';
import {
  CLEARED_MARKETPLACE_FILTERS,
  CONTINUE_TO_SHIPPING_LABEL,
  getLeftoverNothingFitsText,
  getLeftoverWithFitsText,
  MAKE_ROOM_HINT,
  NOTHING_PICKED_YET_TEXT,
  SWAP_PIN_THUMBNAIL_SIZE,
  YOUR_PICKS_TITLE,
} from '../../../constants/marketplace';
import { GALLERY_THUMBNAIL_SIZE, HOVER_MEDIA } from '../../../constants/ui';
import {
  marketplaceRemoveFromCartAction,
  marketplaceSetFiltersAction,
  marketplaceSwapCancelAction,
} from '../../../store/actions/marketplace';
import {
  selectAffordableItemCount,
  selectCreditsRemaining,
  selectMarketplaceCart,
  selectNothingElseFits,
  selectSupplierLock,
  selectSwapPending,
} from '../../../store/selectors/marketplace';
import { IMarketplaceCartLine, ShipsFromNoteVariantEnum } from '../../../types/marketplace';

interface IProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

// Every row is separated, the last one included, so the list closes against the note below.
const cartRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 1.5,
  px: 1,
  mx: -1,
  borderBottom: 1,
  borderColor: 'divider',
};

const thumbnailSx: SxProps<Theme> = {
  flexShrink: 0,
  width: GALLERY_THUMBNAIL_SIZE,
  height: GALLERY_THUMBNAIL_SIZE,
  borderRadius: 0.5,
  border: 1,
  borderColor: 'divider',
  backgroundColor: 'common.white',
  overflow: 'hidden',
};

const swapPinThumbnailSx: SxProps<Theme> = {
  ...thumbnailSx,
  width: SWAP_PIN_THUMBNAIL_SIZE,
  height: SWAP_PIN_THUMBNAIL_SIZE,
};

const thumbnailMediaSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  p: 0.5,
  boxSizing: 'border-box',
};

const lineColumnSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
};

const removeButtonSx: SxProps<Theme> = {
  flexShrink: 0,
  color: 'action.active',
  [HOVER_MEDIA]: {
    '&:hover': {
      color: 'error.main',
      backgroundColor: ({ palette }) => alpha(palette.error.main, palette.action.hoverOpacity),
    },
  },
};

// Sits with the picks rather than in the footer: it belongs to the budget story above the rows.
const leftoverNoticeSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 1.25,
  borderRadius: 0.5,
  backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.hoverOpacity),
};

// Dashed primary outline on a faint ground, so it reads as a placeholder waiting on room.
const swapPinSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  p: 1.5,
  mt: 2,
  borderRadius: 1,
  border: ({ palette }) => `1px dashed ${alpha(palette.primary.main, 0.4)}`,
  backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.hoverOpacity),
};

const footerColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 1,
  width: '100%',
};

const headerColumnSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 2, mt: 2 };

const budgetRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 2,
};

const getVariantKey = (line: IMarketplaceCartLine) => line.variant_id;

interface ICartLineRowProps {
  line: IMarketplaceCartLine;
  onRemove: (variantId: string) => void;
}

const CartLineRow: React.FC<ICartLineRowProps> = ({ line, onRemove }) => {
  const handleRemove = useCallback(() => onRemove(line.variant_id), [onRemove, line.variant_id]);

  return (
    <Box sx={cartRowSx}>
      <Box sx={thumbnailSx}>
        {line.image_url && <CardMedia component="img" image={line.image_url} alt="" sx={thumbnailMediaSx} />}
      </Box>
      <Box sx={lineColumnSx}>
        <Typography variant="body2" fontWeight="medium" noWrap>
          {line.name}
        </Typography>
        {line.vendor && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {line.vendor}
          </Typography>
        )}
        {line.variantLabel && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {line.variantLabel}
          </Typography>
        )}
      </Box>
      <CreditChip credits={line.creditPrice * line.quantity} sx={{ flexShrink: 0 }} />
      <IconButton
        size="small"
        aria-label={`Remove ${line.vendor ?? ''} ${line.name}`.trim()}
        onClick={handleRemove}
        sx={removeButtonSx}
      >
        <DeleteIcon width={20} height={20} aria-hidden />
      </IconButton>
    </Box>
  );
};

export const MarketplaceCartDrawer: React.FC<IProps> = ({ open, onClose, onContinue }) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const cart = useSelector(selectMarketplaceCart);
  const remaining = useSelector(selectCreditsRemaining);
  const affordableCount = useSelector(selectAffordableItemCount);
  const nothingElseFits = useSelector(selectNothingElseFits);
  const swapPending = useSelector(selectSwapPending);
  const supplierLock = useSelector(selectSupplierLock);

  const isCartEmpty = cart.length === 0;
  const hasAffordableItems = affordableCount > 0;
  const isSwapPending = !!swapPending;

  const handleRemove = useCallback(
    (variantId: string) => dispatch(marketplaceRemoveFromCartAction(variantId)),
    [dispatch],
  );

  const handleCancelSwap = useCallback(() => dispatch(marketplaceSwapCancelAction()), [dispatch]);

  const handleSeeWhatFits = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ ...CLEARED_MARKETPLACE_FILTERS, fitsBudget: true }));
    onClose();
  }, [dispatch, onClose]);

  const leftoverMessage = useMemo(() => {
    if (isCartEmpty || remaining <= 0) {
      return null;
    }

    if (nothingElseFits) {
      return getLeftoverNothingFitsText(remaining);
    }

    return hasAffordableItems ? getLeftoverWithFitsText(remaining, affordableCount) : null;
  }, [isCartEmpty, remaining, affordableCount, hasAffordableItems, nothingElseFits]);

  const swapPin = useMemo(() => {
    if (!swapPending) {
      return null;
    }

    return (
      <Box sx={swapPinSx}>
        <Box sx={swapPinThumbnailSx}>
          {swapPending.image_url && (
            <CardMedia component="img" image={swapPending.image_url} alt="" sx={thumbnailMediaSx} />
          )}
        </Box>
        <Box sx={lineColumnSx}>
          <Typography variant="caption" fontWeight="medium" color="primary.main">
            Make room for
          </Typography>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {swapPending.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {MAKE_ROOM_HINT}
          </Typography>
        </Box>
        <IconButton aria-label="Cancel swap" onClick={handleCancelSwap} sx={{ flexShrink: 0, color: 'action.active' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }, [swapPending, handleCancelSwap]);

  const renderCartLine = useCallback(
    (line: IMarketplaceCartLine) => <CartLineRow line={line} onRemove={handleRemove} />,
    [handleRemove],
  );

  // Nothing picked means nothing to continue with, so the footer goes away rather than disabling.
  const footerButton = useMemo(() => {
    if (isCartEmpty) {
      return null;
    }

    return (
      <Box sx={footerColumnSx}>
        {isSwapPending && (
          <Typography variant="body2" color="text.secondary">
            Remove a pick to make room
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
          fullWidth={isMobileViewport}
          disabled={isSwapPending}
          onClick={onContinue}
        >
          {CONTINUE_TO_SHIPPING_LABEL}
        </Button>
      </Box>
    );
  }, [isCartEmpty, onContinue, isMobileViewport, isSwapPending]);

  const headerContent = useMemo(
    () => (
      <Box sx={headerColumnSx}>
        <Box sx={budgetRowSx}>
          <Typography variant="body2" fontWeight="medium" color="text.secondary">
            Your budget
          </Typography>
          <WalletMeter remaining={remaining} />
        </Box>
        {leftoverMessage && (
          <Box sx={leftoverNoticeSx}>
            <TollOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              {leftoverMessage}
            </Typography>
            {hasAffordableItems && (
              <Button variant="text" color="primary" size="small" onClick={handleSeeWhatFits} sx={{ flexShrink: 0 }}>
                See what fits
              </Button>
            )}
          </Box>
        )}
        {isCartEmpty && (
          <Typography variant="body2" color="text.secondary">
            {NOTHING_PICKED_YET_TEXT}
          </Typography>
        )}
        {swapPin}
      </Box>
    ),
    [remaining, isCartEmpty, leftoverMessage, hasAffordableItems, handleSeeWhatFits, swapPin],
  );

  // Every pick ships from the locked supplier, so the note names it like the lock banner does.
  const shipsFromNote = useMemo(() => {
    if (isCartEmpty) {
      return null;
    }

    return supplierLock?.name ? (
      <ShipsFromNote vendor={supplierLock.name} />
    ) : (
      <ShipsFromNote variant={ShipsFromNoteVariantEnum.AllPartners} />
    );
  }, [isCartEmpty, supplierLock?.name]);

  return (
    <ViewDetailsSidebar<IMarketplaceCartLine>
      open={open}
      onClose={onClose}
      title={YOUR_PICKS_TITLE}
      items={cart}
      renderItem={renderCartLine}
      getItemKey={getVariantKey}
      footerButton={footerButton}
      headerContent={headerContent}
      bodyFooterContent={shipsFromNote}
      hideItemDividers
      hideItemsCount
      isFixedHeightOnMobile
    />
  );
};
